import React, { useState, useCallback } from 'react';
import {
  Button, 
  TextField, 
  FormControlLabel, 
  Checkbox, 
  Link, 
  Typography,
  Box, 
  Grid, 
  Container, 
  FormHelperText,
  Alert,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useStatus } from '../providers/MsgStatusProvider';
import { validateEmail } from '../utils/formValidation';
import { logError, logInfo, logWarning } from '../utils/logger';
import WaitlistStatusCard from '../components/WaitlistStatusCard';
import WaitlistSignupCard from '../components/WaitlistSignupCard';

const StyledForm = styled('form')(({ theme }) => ({
  width: '100%',
  marginTop: theme.spacing(3),
}));

const RequiredLabel = styled('span')(({ theme }) => ({
  '&::after': {
    content: '" *"',
    color: theme.palette.error.main,
  },
}));

/**
 * Register component for user registration with comprehensive validation
 * Handles waitlist signup process and form validation edge cases
 * 
 * @component
 */
function Register() {
    const navigate = useNavigate();
    const { setStatusMessage, setStatusType, setIsAlertOpen } = useStatus();
    
    // Form state
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        connection: ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Waitlist state
    const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
    const [waitlistData, setWaitlistData] = useState(null);
    const [showJoinWaitlist, setShowJoinWaitlist] = useState(false);

    /**
     * Handles form field changes with real-time validation
     * @param {Event} event - The change event
     */
    const handleChange = useCallback((event) => {
        const { name, value } = event.target;
        
        try {
            setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
            
            // Mark field as touched when user starts typing
            if (value.length > 0) {
                setTouched(prev => ({ ...prev, [name]: true }));
            }
            
            // Clear field error when user starts typing
            if (errors[name]) {
                setErrors(prev => ({ ...prev, [name]: '' }));
            }
        } catch (error) {
            logError('Form field change error', { fieldName: name, error: error.message });
        }
    }, [errors]);

    /**
     * Handles field blur events for validation
     * @param {Event} event - The blur event
     */
    const handleBlur = useCallback((event) => {
        const { name, value } = event.target;
        
        setTouched(prev => ({ ...prev, [name]: true }));
        
        // Validate email field on blur
        if (name === 'email' && value) {
            const emailError = validateEmail(value);
            setErrors(prev => ({
                ...prev,
                [name]: emailError || ''
            }));
        }
    }, []);

    /**
     * Validates the registration form
     * @returns {boolean} True if form is valid
     */
    const validateForm = useCallback(() => {
        const newErrors = {};
        
        try {
            // Email validation with enhanced security checks
            if (!formData.email || formData.email.trim().length === 0) {
                newErrors.email = 'Email is required';
            } else {
                const emailError = validateEmail(formData.email);
                if (emailError) {
                    newErrors.email = emailError;
                }
            }
            
            // Terms and conditions validation
            if (!agreeToTerms) {
                newErrors.agreeToTerms = 'You must agree to the terms and conditions to continue';
            }

            setErrors(newErrors);
            
            // Log validation attempt for monitoring
            const isValid = Object.keys(newErrors).length === 0;
            logInfo('Registration form validation', {
                isValid,
                errorCount: Object.keys(newErrors).length,
                hasEmail: Boolean(formData.email),
                agreeToTerms
            });
            
            return isValid;
        } catch (error) {
            logError('Registration form validation error', { error: error.message });
            setErrors({ _general: 'Validation error occurred. Please try again.' });
            return false;
        }
    }, [formData.email, agreeToTerms]);

    /**
     * Handles form submission
     * @param {Event} event - The submit event
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        
        if (!validateForm()) {
            // Focus on first error field for better UX
            const firstErrorField = Object.keys(errors)[0];
            if (firstErrorField && firstErrorField !== 'agreeToTerms') {
                const element = document.getElementById(firstErrorField);
                if (element) {
                    element.focus();
                }
            }
            return;
        }

        setIsSubmitting(true);
        
        try {
            const url = `${process.env.REACT_APP_API_BASE_URL}user/register/email`;
            
            logInfo('Registration attempt started', { 
                email: formData.email,
                hasTermsAgreement: agreeToTerms 
            });
            
            const response = await fetch(url, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'User-Agent': navigator.userAgent
                },
                body: JSON.stringify({
                    email: formData.email.trim().toLowerCase(),
                })
            });

            const data = await response.json();

            if (response.ok) {
                if (data.code === 808) {
                    // User already registered
                    setIsAlertOpen(true);
                    setStatusType('success');
                    setStatusMessage(data.message);
                    
                    logInfo('Registration successful - existing user', { 
                        email: formData.email,
                        code: data.code 
                    });
                } else {
                    // New registration
                    localStorage.setItem('token', data.token);
                    setIsAlertOpen(true);
                    setStatusMessage('Registration successful! Please check your email to verify your account.');
                    setStatusType('success');
                    
                    logInfo('Registration successful - new user', { 
                        email: formData.email,
                        hasToken: Boolean(data.token)
                    });
                    
                    navigate('/check-email');
                }
            } else {
                throw new Error(data.message || 'Registration failed');
            }
        } catch (error) {
            console.error('Registration error:', error);
            
            // Handle specific error cases
            if (error.message === "You're not on the waitlist") {
                logWarning('User not on waitlist', { email: formData.email });
                setShowJoinWaitlist(true);
            } else if (error.message.includes('rate limit') || error.message.includes('too many')) {
                logWarning('Rate limit exceeded during registration', { email: formData.email });
                setStatusMessage('Too many registration attempts. Please try again in a few minutes.');
                setStatusType('warning');
                setIsAlertOpen(true);
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                logError('Network error during registration', { 
                    error: error.message,
                    email: formData.email 
                });
                setStatusMessage('Network error. Please check your connection and try again.');
                setStatusType('error');
                setIsAlertOpen(true);
            } else {
                logError('Registration error', { 
                    error: error.message,
                    email: formData.email 
                });
                setStatusMessage(error.message || 'Registration failed. Please try again.');
                setStatusType('error');
                setIsAlertOpen(true);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Gets the error message for a specific field
     * @param {string} fieldName - Field name
     * @returns {string} Error message or empty string
     */
    const getFieldError = useCallback((fieldName) => {
        return errors[fieldName] || '';
    }, [errors]);

    /**
     * Checks if a field has an error and is touched
     * @param {string} fieldName - Field name
     * @returns {boolean} True if field has error
     */
    const hasFieldError = useCallback((fieldName) => {
        return Boolean(errors[fieldName] && (touched[fieldName] || Object.keys(errors).length > 0));
    }, [errors, touched]);

    return (
        <Container component="main" maxWidth="md">
            {waitlistData ? (
                <WaitlistStatusCard waitlistData={waitlistData} />
            ) : showJoinWaitlist ? (
                <WaitlistSignupCard email={formData.email} />
            ) : (
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: 500,
                        mx: 'auto'
                    }}
                >
                    <Typography component="h1" variant="h3" gutterBottom align="center">
                        Join Solo Social
                    </Typography>
                    
                    <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
                        Enter your email to get started with SoloSocial
                    </Typography>

                    {/* General error alert */}
                    {errors._general && (
                        <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                            {errors._general}
                        </Alert>
                    )}

                    <StyledForm onSubmit={handleSubmit} noValidate>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    name="email"
                                    type="email"
                                    label={<RequiredLabel>Email Address</RequiredLabel>}
                                    autoComplete="email"
                                    autoFocus
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={hasFieldError('email')}
                                    helperText={
                                        getFieldError('email') || 
                                        'Enter a valid email address to create your account'
                                    }
                                    disabled={isSubmitting}
                                    inputProps={{
                                        maxLength: 254,
                                        'aria-describedby': 'email-helper-text'
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={agreeToTerms}
                                            onChange={(e) => {
                                                setAgreeToTerms(e.target.checked);
                                                // Clear error when user checks the box
                                                if (e.target.checked && errors.agreeToTerms) {
                                                    setErrors(prev => ({ ...prev, agreeToTerms: '' }));
                                                }
                                            }}
                                            name="agreeToTerms"
                                            color="primary"
                                            disabled={isSubmitting}
                                            required
                                            inputProps={{
                                                'aria-describedby': 'terms-helper-text'
                                            }}
                                        />
                                    }
                                    label={
                                        <Typography variant="body2">
                                            <RequiredLabel>I agree to the</RequiredLabel>{" "}
                                            <Link 
                                                href="https://www.onsolosocial.com/terms-of-service" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                sx={{ textDecoration: 'none' }}
                                            >
                                                Terms of Service
                                            </Link>{" "}
                                            and{" "}
                                            <Link 
                                                href="https://www.onsolosocial.com/privacy-policy" 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                sx={{ textDecoration: 'none' }}
                                            >
                                                Privacy Policy
                                            </Link>
                                        </Typography>
                                    }
                                />
                                {errors.agreeToTerms && (
                                    <FormHelperText error id="terms-helper-text">
                                        {errors.agreeToTerms}
                                    </FormHelperText>
                                )}
                            </Grid>
                        </Grid>
                        
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{ 
                                mt: 3, 
                                mb: 2,
                                height: 48,
                                position: 'relative'
                            }}
                            aria-describedby="submit-button-helper"
                        >
                            {isSubmitting ? (
                                <>
                                    <CircularProgress 
                                        size={20} 
                                        sx={{ 
                                            position: 'absolute',
                                            left: '50%',
                                            marginLeft: '-10px'
                                        }} 
                                    />
                                    <span style={{ opacity: 0 }}>Register</span>
                                </>
                            ) : (
                                'Register'
                            )}
                        </Button>
                        
                        <Typography variant="body2" color="text.secondary" align="center">
                            Already have an account?{' '}
                            <Link 
                                href="/login" 
                                sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                            >
                                Sign in here
                            </Link>
                        </Typography>
                    </StyledForm>

                    {/* Validation Summary for Development */}
                    {process.env.NODE_ENV === 'development' && Object.keys(errors).length > 0 && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, width: '100%' }}>
                            <Typography variant="caption" display="block" color="error">
                                Validation Errors: {Object.keys(errors).join(', ')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Container>
    );
}

export default Register;